/**
 * 应用路由：公共 / 受保护（已登录 + 管理员）路由与守卫。
 *
 * - 未登录访问受保护路由 → 重定向 /login
 * - 已登录访问 /login、/register → 重定向 /
 * - 管理员页面额外校验 user.role === 'admin'
 *
 * @module router
 */
import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuthStore } from '@/store/authStore';
import MainLayout from '@/layouts/MainLayout';
import AuthLayout from '@/layouts/AuthLayout';

import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import DashboardPage from '@/pages/DashboardPage';
import ApiKeyPage from '@/pages/ApiKeyPage';
import UsagePage from '@/pages/UsagePage';
import RechargePage from '@/pages/RechargePage';
import PackagePage from '@/pages/PackagePage';
import TeamPage from '@/pages/TeamPage';
import RefundPage from '@/pages/RefundPage';
import ChannelAdminPage from '@/pages/admin/ChannelAdminPage';
import ModelMappingAdminPage from '@/pages/admin/ModelMappingAdminPage';
import UserAdminPage from '@/pages/admin/UserAdminPage';
import RefundAdminPage from '@/pages/admin/RefundAdminPage';
import ConfigAdminPage from '@/pages/admin/ConfigAdminPage';

/**
 * 已登录守卫：未登录重定向 /login，否则渲染 MainLayout（含 Outlet）。
 *
 * @returns JSX 元素。
 */
function RequireAuth(): JSX.Element {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <MainLayout />;
}

/**
 * 管理员守卫：未登录重定向 /login，非 admin 重定向 /。
 *
 * @returns JSX 元素。
 */
function RequireAdmin(): JSX.Element {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (role !== 'admin') return <Navigate to="/" replace />;
  return <MainLayout />;
}

/**
 * 公共页守卫：已登录用户访问 /login、/register 时重定向 /。
 *
 * @param props 组件属性。
 * @returns JSX 元素。
 */
function PublicOnly({ children }: { children: ReactNode }): JSX.Element {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * 应用路由表。
 *
 * @returns JSX 元素。
 */
export default function AppRouter(): JSX.Element {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <AuthLayout title="登录">
              <LoginPage />
            </AuthLayout>
          </PublicOnly>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnly>
            <AuthLayout title="注册">
              <RegisterPage />
            </AuthLayout>
          </PublicOnly>
        }
      />

      <Route element={<RequireAuth />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/apikeys" element={<ApiKeyPage />} />
        <Route path="/usage" element={<UsagePage />} />
        <Route path="/recharge" element={<RechargePage />} />
        <Route path="/packages" element={<PackagePage />} />
        <Route path="/teams" element={<TeamPage />} />
        <Route path="/refunds" element={<RefundPage />} />
      </Route>

      <Route element={<RequireAdmin />}>
        <Route path="/admin/channels" element={<ChannelAdminPage />} />
        <Route path="/admin/model-mappings" element={<ModelMappingAdminPage />} />
        <Route path="/admin/users" element={<UserAdminPage />} />
        <Route path="/admin/refunds" element={<RefundAdminPage />} />
        <Route path="/admin/configs" element={<ConfigAdminPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
